import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Tabs, Tab, Card, CardContent, IconButton } from '@mui/material';
import { BellIcon, WalletIcon, ShieldAlertIcon, ArrowRightLeftIcon, TrendingUpIcon, SettingsIcon, CheckIcon } from 'lucide-react';
const NotificationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  // Mock Data
  const notifications = [{
    id: 1,
    category: 'Balance',
    title: 'Float Threshold Alert',
    desc: 'PKR float for Branch BR-0001 has dropped below the 20% minimum threshold. Current available balance is 1,200,000 PKR.',
    time: '10 mins ago',
    isRead: false,
    icon: <WalletIcon size={20} color="#B38600" data-id="element-2307" />,
    iconBg: '#FFFBEB',
    link: 'View Float Details'
  }, {
    id: 2,
    category: 'Compliance',
    title: 'Transaction Flagged for Review',
    desc: 'Transaction TXN-20250101-005 has been flagged by the automated compliance system for potential OFAC match.',
    time: '1 hour ago',
    isRead: false,
    icon: <ShieldAlertIcon size={20} color="#AE2A19" data-id="element-2308" />,
    iconBg: '#FEF2F2',
    link: 'Review Transaction'
  }, {
    id: 3,
    category: 'Settlement',
    title: 'Daily Settlement Completed',
    desc: 'End of day settlement for 2025-01-01 has been successfully processed and reconciled.',
    time: '3 hours ago',
    isRead: true,
    icon: <ArrowRightLeftIcon size={20} color="#0B7923" data-id="element-2309" />,
    iconBg: '#ECFDF5',
    link: 'View Settlement Report'
  }, {
    id: 4,
    category: 'Rates',
    title: 'FX Rate Updated',
    desc: 'The USD to PKR exchange rate has been updated to 278.50. This rate will be valid for the next 4 hours.',
    time: '5 hours ago',
    isRead: true,
    icon: <TrendingUpIcon size={20} color="#0052CC" data-id="element-2310" />,
    iconBg: '#E8F0FE',
    link: 'View Rate History'
  }, {
    id: 5,
    category: 'System',
    title: 'Scheduled Maintenance',
    desc: 'The system will undergo scheduled maintenance on Sunday, Jan 5th from 02:00 AM to 04:00 AM UTC.',
    time: '1 day ago',
    isRead: true,
    icon: <SettingsIcon size={20} color="#6B7280" data-id="element-2311" />,
    iconBg: '#F3F5F7'
  }];
  const categories = ['All', 'Balance', 'Compliance', 'Settlement', 'Rates', 'System'];
  const filteredNotifications = activeTab === 0 ? notifications : notifications.filter(n => n.category === categories[activeTab]);
  return <Box sx={{
    p: 3,
    bgcolor: '#F8F9FA',
    minHeight: '100vh',
    maxWidth: 1000,
    mx: 'auto'
  }} data-id="element-2312">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 4
    }} data-id="element-2313">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }} data-id="element-2314">
          <BellIcon size={28} color="#1F2937" data-id="element-2315" />
          <Typography variant="h4" sx={{
          fontWeight: 700,
          color: '#1F2937'
        }} data-id="element-2316">
            Notifications
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<CheckIcon size={18} data-id="element-2318" />} sx={{
        color: '#374151',
        borderColor: '#E5E7EB',
        bgcolor: '#FFFFFF',
        '&:hover': {
          bgcolor: '#F9FAFB'
        }
      }} data-id="element-2317">
          Mark All as Read
        </Button>
      </Box>

      <Paper elevation={0} sx={{
      borderRadius: 2,
      border: '1px solid #E5E7EB',
      mb: 4
    }} data-id="element-2319">
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          color: '#6B7280',
          minWidth: 120,
          '&.Mui-selected': {
            color: '#0052CC'
          }
        },
        '& .MuiTabs-indicator': {
          backgroundColor: '#0052CC'
        }
      }} data-id="element-2320">
          {categories.map((cat, index) => <Tab key={index} label={cat} data-id="element-2321" />)}
        </Tabs>
      </Paper>

      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }} data-id="element-2322">
        {filteredNotifications.map(notif => <Card key={notif.id} elevation={0} sx={{
        borderRadius: 2,
        border: '1px solid #E5E7EB',
        borderLeft: notif.isRead ? '1px solid #E5E7EB' : '3px solid #0052CC',
        bgcolor: notif.isRead ? '#FFFFFF' : '#F0F3F7',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: '0px 2px 8px rgba(0,0,0,0.05)'
        }
      }} data-id="element-2323">
            <CardContent sx={{
          p: 3,
          display: 'flex',
          gap: 3,
          alignItems: 'flex-start'
        }} data-id="element-2324">
              <Box sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: notif.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }} data-id="element-2325">
                {notif.icon}
              </Box>
              <Box sx={{
            flexGrow: 1
          }} data-id="element-2326">
                <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1
            }} data-id="element-2327">
                  <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                color: '#1F2937'
              }} data-id="element-2328">
                    {notif.title}
                  </Typography>
                  <Typography variant="caption" sx={{
                color: '#9CA3AF',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }} data-id="element-2329">
                    {notif.time}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{
              color: '#374151',
              mb: 2,
              lineHeight: 1.5
            }} data-id="element-2330">
                  {notif.desc}
                </Typography>
                {notif.link && <Button variant="text" size="small" sx={{
              color: '#0052CC',
              fontWeight: 600,
              p: 0,
              '&:hover': {
                bgcolor: 'transparent',
                textDecoration: 'underline'
              }
            }} data-id="element-2331">
                    {notif.link}
                  </Button>}
              </Box>
            </CardContent>
          </Card>)}

        {filteredNotifications.length === 0 && <Box sx={{
        textAlign: 'center',
        py: 8
      }} data-id="element-2332">
            <BellIcon size={48} color="#E5E7EB" style={{
          margin: '0 auto',
          marginBottom: 16
        }} data-id="element-2333" />
            <Typography variant="h6" sx={{
          color: '#6B7280',
          fontWeight: 600
        }} data-id="element-2334">
              No notifications found
            </Typography>
            <Typography variant="body2" sx={{
          color: '#9CA3AF'
        }} data-id="element-2335">
              You're all caught up in this category.
            </Typography>
          </Box>}
      </Box>
    </Box>;
};
export default NotificationsPage;